import { useTheme } from '@mui/material';

import { colors, COLORS } from '@constants/colors';
import { ScreenSizes } from '@interfaces';

import { MAP_EDITOR_STATUS_COLORS } from '../MapEditor/styles';

const CONTROL_HEIGHT = 40;
const CONTROL_HEIGHT_MOBILE = 32;

/**
 * Style map for the Editor Mode top bar (BrandingBar) and its QuickSwitcher popup.
 *
 * Mirrors the shrink breakpoint the rest of the top chrome row already uses
 * (`DashboardBarButton`, `HomeBarButton`, `ParticipantsButton`) — this bar
 * previously had no `breakpoints.down(ScreenSizes.TabletHorizontal)` rules at
 * all, so at narrow widths it stayed full size while its neighbours shrank,
 * throwing the row out of alignment and overflowing its `maxWidth`.
 */
export const useBrandingBarStyles = () => {
  const { breakpoints } = useTheme();
  const shrink = breakpoints.down(ScreenSizes.TabletHorizontal);

  return {
    // Pill matching the top chrome row (MytaverseAppBar root): same height,
    // radius, background and border as the location pill it sits next to.
    bar: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '7px',
      height: '56px',
      borderRadius: '99px',
      background: colors.black_99,
      backdropFilter: 'blur(12px)',
      border: '1px solid rgba(255, 255, 255, 0.25)',
      color: COLORS.WHITE,
      flexWrap: 'nowrap',
      whiteSpace: 'nowrap',
      boxSizing: 'border-box',
      width: 'fit-content',
      maxWidth: 'calc(100vw - 48px)',
      [shrink]: {
        gap: '4px',
        padding: '5px',
        height: '44px',
      },
    },
    brandButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      height: CONTROL_HEIGHT,
      cursor: 'pointer',
      padding: '0 8px 0 7px',
      borderRadius: '99px',
      color: COLORS.WHITE,
      flexShrink: 1,
      minWidth: 0,
      transition: 'background-color 0.15s ease',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
      },
      [shrink]: {
        gap: '6px',
        height: CONTROL_HEIGHT_MOBILE,
        padding: '0 6px 0 5px',
      },
    },
    brandSwatchRow: {
      display: 'flex',
      width: 32,
      height: 20,
      borderRadius: '5px',
      overflow: 'hidden',
      gap: '1px',
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      flexShrink: 0,
      [shrink]: {
        width: 26,
        height: 16,
      },
    },
    brandSwatchStripe: {
      flex: 1,
      height: '100%',
    },
    brandSwatchPlaceholder: {
      flex: 1,
      height: '100%',
      background:
        'repeating-linear-gradient(45deg, rgba(58,58,70,1) 0px, rgba(58,58,70,1) 5px, rgba(46,46,58,1) 5px, rgba(46,46,58,1) 10px)',
    },
    brandName: {
      color: COLORS.WHITE,
      fontSize: '13px',
      fontWeight: 600,
      maxWidth: '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      [shrink]: {
        fontSize: '12px',
        maxWidth: '100px',
      },
    },
    divider: {
      width: '1px',
      height: 20,
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
      flexShrink: 0,
      [shrink]: {
        height: 16,
      },
    },
    autosaveRow: {
      display: 'flex',
      alignItems: 'center',
      // Centred inside the reserved width, which is the only setting that pairs with the floor below.
      // Left-aligned, a status shorter than the floor sat against the left divider and left the slack
      // as a hole before the right one — 4px on one side and 59px on the other for `Layout draft`,
      // 4 and 72 for `Saving…`, measured in Chromium. It reads as the label having slipped out of the
      // middle of the bar, and it moves as the status changes, which is what makes it noticeable.
      justifyContent: 'center',
      gap: '5px',
      color: colors.white_50,
      fontSize: '11px',
      whiteSpace: 'nowrap',
      padding: '0 4px',
      flexShrink: 0,
      // A floor rather than a fixed width: the fixed one kept the bar from jumping as the status
      // changes, which a floor still does, but it also clipped anything longer than the English
      // phrases — and this row now carries a fourth state in 22 more languages.
      minWidth: '124px',
      [shrink]: {
        fontSize: '10px',
        minWidth: '96px',
      },
    },
    autosaveDot: {
      width: 6,
      height: 6,
      borderRadius: '50%',
      flexShrink: 0,
    },
    // The unpublished-layout state, in the amber the walk banner already uses for it — same state,
    // same colour, wherever the author reads it (see MAP_EDITOR_STATUS_COLORS). One element rather
    // than the fragment the other states return, because the Tooltip over it needs a single child to
    // hold its ref.
    autosaveDraft: {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      color: MAP_EDITOR_STATUS_COLORS.draftText,
      cursor: 'default',
    },

    activateButton: {
      color: COLORS.WHITE,
      backgroundColor: COLORS.MYTAVERSE_BLUE,
      textTransform: 'none',
      height: CONTROL_HEIGHT,
      padding: '0 14px',
      borderRadius: '9px',
      fontSize: '12px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      minWidth: 'unset',
      flexShrink: 0,
      boxShadow: 'none',
      '&:hover': { backgroundColor: '#3A46C1', boxShadow: 'none' },
      '&.Mui-disabled': {
        backgroundColor: 'rgba(69, 84, 229, 0.35)',
        color: 'rgba(255, 255, 255, 0.55)',
      },
      [shrink]: {
        height: CONTROL_HEIGHT_MOBILE,
        padding: '0 10px',
        fontSize: '11px',
      },
    },
    activeLabel: {
      color: COLORS.GREEN,
      fontSize: '12px',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      whiteSpace: 'nowrap',
      padding: '0 6px',
      flexShrink: 0,
      [shrink]: {
        fontSize: '11px',
        gap: '4px',
        padding: '0 4px',
      },
    },
    activeLabelInactive: {
      color: colors.white_50,
    },
    activeLabelDot: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      backgroundColor: COLORS.GREEN,
      boxShadow: '0 0 6px rgba(29, 171, 86, 0.9)',
      [shrink]: {
        width: 6,
        height: 6,
      },
    },
    activeLabelDotInactive: {
      backgroundColor: colors.white_40,
      boxShadow: 'none',
    },
    // The way out of the walk, drawn only while the author is in it. It lives on this bar rather than
    // on a banner of its own because the two were centred on the same spot and overlapped; and once
    // they share a row, the bar's status line is already saying whether the layout is published, so
    // this carries the exit alone and leaves the state to the row that owns it.
    backToPlan: {
      height: '30px',
      padding: '0 12px',
      borderRadius: '8px',
      border: '1px solid rgba(255, 255, 255, 0.24)',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: COLORS.WHITE,
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'none',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      '&:hover': {
        borderColor: 'rgba(255, 255, 255, 0.4)',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
      [shrink]: {
        height: '26px',
        padding: '0 8px',
        fontSize: '11px',
      },
    },
    // Amber while the walk is through unpublished work. The distinction the banner carried in prose is
    // kept as colour: a walk through a draft must not read as a walk through the live world, and the
    // status row next to it spells out which one it is.
    backToPlanDraft: {
      borderColor: `${MAP_EDITOR_STATUS_COLORS.draftDot}6B`,
      backgroundColor: 'rgba(255, 184, 0, 0.14)',
      color: MAP_EDITOR_STATUS_COLORS.draftText,
      '&:hover': {
        borderColor: MAP_EDITOR_STATUS_COLORS.draftDot,
        backgroundColor: 'rgba(255, 184, 0, 0.22)',
      },
    },
    backToPlanTooltipTitle: {
      fontSize: '12.5px',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    backToPlanTooltipHint: {
      fontSize: '11px',
      lineHeight: 1.35,
      color: colors.white_60,
    },
    exitButton: {
      color: colors.white_60,
      width: CONTROL_HEIGHT,
      height: CONTROL_HEIGHT,
      borderRadius: '50%',
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.14)',
      flexShrink: 0,
      '&:hover': {
        color: COLORS.WHITE,
        borderColor: 'rgba(255, 255, 255, 0.3)',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
      },
      [shrink]: {
        width: CONTROL_HEIGHT_MOBILE,
        height: CONTROL_HEIGHT_MOBILE,
      },
    },
    exitIcon: {
      fontSize: 20,
      [shrink]: {
        fontSize: 16,
      },
    },

    /* QuickSwitcher */
    quickSwitcherPaper: {
      backgroundColor: 'rgba(20, 20, 24, 0.97)',
      backdropFilter: 'blur(18px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '12px',
      width: 320,
      overflow: 'hidden',
      marginTop: '8px',
    },
    quickSearchWrap: {
      padding: '10px 12px 8px',
    },
    quickSearchField: {
      '& .MuiOutlinedInput-root': {
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
        borderRadius: '8px',
        color: COLORS.WHITE,
        fontSize: '13px',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.1)' },
        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.18)' },
        '&.Mui-focused fieldset': { borderColor: COLORS.MYTAVERSE_BLUE },
      },
      '& .MuiOutlinedInput-input': { padding: '7px 6px' },
    },
    quickList: {
      maxHeight: '300px',
      overflowY: 'auto',
      padding: '4px',
    },
    quickRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '6px 8px',
      borderRadius: '8px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
      },
    },
    quickRowActive: {
      backgroundColor: 'rgba(69, 84, 229, 0.18)',
      '&:hover': { backgroundColor: 'rgba(69, 84, 229, 0.24)' },
    },
    quickName: {
      flex: 1,
      color: COLORS.WHITE,
      fontSize: '13px',
      fontWeight: 500,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    quickLiveDot: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      backgroundColor: COLORS.GREEN,
      flexShrink: 0,
    },
    /* Status slot keeps the right column the same width whether the row shows
     the static "currently live" indicator or the clickable activate button,
     so all icons vertically align across rows. */
    quickStatusSlot: {
      width: 30,
      height: 30,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      cursor: 'default',
    },
    quickStatusButton: {
      width: 30,
      height: 30,
      padding: 0,
      color: colors.white_50,
      flexShrink: 0,
      '&:hover': {
        color: COLORS.GREEN,
        backgroundColor: 'rgba(29, 171, 86, 0.12)',
      },
    },
    quickStatusLiveIcon: {
      fontSize: 18,
      color: COLORS.GREEN,
    },
    quickStatusInactiveIcon: {
      fontSize: 18,
      color: 'inherit',
    },
    quickPowerButton: {
      // kept for backwards-compat with anything that may still reference it
      color: colors.white_50,
      padding: '4px',
    },
    quickFooter: {
      display: 'flex',
      gap: '8px',
      padding: '8px 10px 10px',
      borderTop: '1px solid rgba(255, 255, 255, 0.06)',
    },
    quickFooterButton: {
      flex: 1,
      color: COLORS.WHITE,
      borderColor: 'rgba(255, 255, 255, 0.16)',
      textTransform: 'none',
      padding: '6px 10px',
      borderRadius: '8px',
      fontSize: '12px',
      fontWeight: 500,
      '&:hover': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
      },
    },
    quickEmpty: {
      color: colors.white_40,
      fontSize: '12px',
      padding: '14px 12px',
      textAlign: 'center',
      fontStyle: 'italic',
    },
    quickLoader: {
      display: 'flex',
      justifyContent: 'center',
      padding: '14px 12px',
    },
  };
};
